import { useTranslation } from 'react-i18next';
import { isRTL } from '../i18n';

/**
 * Hook to determine if current language is RTL
 */
export const useDirection = () => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  
  return {
    isRTL: isRTL(currentLanguage),
    direction: isRTL(currentLanguage) ? 'rtl' : 'ltr',
    language: currentLanguage,
  };
};

/**
 * Helper function to conditionally apply classes based on text direction
 */
export const useDirectionalClass = () => {
  const { isRTL: isRightToLeft } = useDirection();
  
  return (ltrClass: string, rtlClass?: string) => {
    if (!rtlClass) return ltrClass;
    return isRightToLeft ? rtlClass : ltrClass;
  };
};

/**
 * Helper function for margin/padding classes that need RTL variants
 */
export const useDirectionalSpacing = () => {
  const dirClass = useDirectionalClass();
  
  return {
    ml: (value: string) => dirClass(`ml-${value}`, `mr-${value}`),
    mr: (value: string) => dirClass(`mr-${value}`, `ml-${value}`),
    pl: (value: string) => dirClass(`pl-${value}`, `pr-${value}`),
    pr: (value: string) => dirClass(`pr-${value}`, `pl-${value}`),
    borderL: dirClass('border-l', 'border-r'),
    borderR: dirClass('border-r', 'border-l'),
    textAlign: {
      left: dirClass('text-left', 'text-right'),
      right: dirClass('text-right', 'text-left'),
    },
    float: {
      left: dirClass('float-left', 'float-right'),
      right: dirClass('float-right', 'float-left'),
    },
  };
};